'use server';

/**
 * @fileOverview A car problem diagnosis AI agent.
 *
 * - diagnoseCarProblems - A function that handles the car diagnosis process.
 * - DiagnoseCarProblemsInput - The input type for the diagnoseCarProblems function.
 * - DiagnoseCarProblemsOutput - The return type for the diagnoseCarProblems function.
 */

import {ai} from '@/ai/genkit';
import {z} from 'genkit';

const DiagnoseCarProblemsInputSchema = z.object({
  symptoms: z
    .string()
    .describe('The symptoms or issues the user is experiencing with their car.'),
});
export type DiagnoseCarProblemsInput = z.infer<typeof DiagnoseCarProblemsInputSchema>;

const DiagnoseCarProblemsOutputSchema = z.object({
  diagnosis: z.string().describe('A preliminary diagnosis of the car problem.'),
  potentialSolutions: z
    .string()
    .describe('Potential solutions or steps the user can take to address the problem.'),
});
export type DiagnoseCarProblemsOutput = z.infer<typeof DiagnoseCarProblemsOutputSchema>;

export async function diagnoseCarProblems(input: DiagnoseCarProblemsInput): Promise<DiagnoseCarProblemsOutput> {
  return diagnoseCarProblemsFlow(input);
}

const prompt = ai.definePrompt({
  name: 'diagnoseCarProblemsPrompt',
  input: {schema: DiagnoseCarProblemsInputSchema},
  output: {schema: DiagnoseCarProblemsOutputSchema},
  prompt: `You are an expert mechanic specializing in diagnosing car issues.

You will use the provided symptoms to give a preliminary diagnosis and suggest potential solutions.

Symptoms: {{{symptoms}}}`,
});

const diagnoseCarProblemsFlow = ai.defineFlow(
  {
    name: 'diagnoseCarProblemsFlow',
    inputSchema: DiagnoseCarProblemsInputSchema,
    outputSchema: DiagnoseCarProblemsOutputSchema,
  },
  async input => {
    const {output} = await prompt(input);
    return output!;
  }
);
